import type { MeetlyCredits } from '../api/meetly';

type MeterCredits = Pick<MeetlyCredits, 'ok' | 'limit' | 'used' | 'remaining' | 'resets_on'> & {
  reserved?: number | null;
  limit: number | null;
  used: number | null;
  remaining: number | null;
  /** The company-wide pool Teamlio granted, when there is one. */
  workspace?: { limit: number; used: number; reserved: number; remaining: number } | null;
};

// The fallback deliberately does NOT name a day. A granted workspace's month
// runs from the day Teamlio granted it, not from the 1st, so "at the start of
// next month" would be wrong for most of them whenever the date is missing.
const resetLabel = (date: string | null | undefined) => {
  if (!date) return 'when the period rolls over';
  const parsed = new Date(`${date}T00:00:00`);
  return Number.isNaN(parsed.getTime())
    ? 'when the period rolls over'
    : `on ${parsed.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}`;
};

export default function MeetlyCreditMeter({ credits }: { credits: MeterCredits }) {
  if (!credits.ok || credits.limit === null || credits.used === null || credits.remaining === null) {
    return (
      <section className="rounded-2xl border border-[var(--gt-border)] bg-white px-4 py-3" aria-live="polite">
        <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[var(--gt-text-tertiary)]">Meetly credits</p>
        <p className="mt-1 text-sm font-semibold text-[var(--gt-text-secondary)]">Usage unavailable</p>
      </section>
    );
  }

  const progress = credits.limit > 0
    ? Math.min(100, Math.max(0, (credits.used / credits.limit) * 100))
    : 100;

  return (
    <section className="rounded-2xl border border-[var(--gt-accent-soft)] bg-[var(--gt-primary-container)] px-4 py-3" aria-label="Meetly credits">
      <div className="flex items-baseline justify-between gap-4">
        <p className="text-sm font-bold text-[var(--gt-primary-deep)]">
          {credits.remaining} of {credits.limit} meetings left this month
        </p>
        <p className="shrink-0 text-xs text-[var(--gt-text-secondary)]">Resets {resetLabel(credits.resets_on)}</p>
      </div>
      <div
        className="mt-2 h-1.5 overflow-hidden rounded-full bg-white/80"
        role="progressbar"
        aria-label="Meetly credit usage"
        aria-valuemin={0}
        aria-valuemax={credits.limit}
        aria-valuenow={credits.used}
      >
        <span className="block h-full rounded-full bg-[var(--gt-primary)]" style={{ width: `${progress}%` }} />
      </div>
      {/* The company pool underneath the personal bar. Without it "8 of 10 left"
          is a half-truth: whoever spends the workspace's last meeting stops
          everyone, so a person can be refused while their own bar looks fine. */}
      {credits.workspace && (
        <p className="mt-2 text-xs text-[var(--gt-text-secondary)]">
          Workspace pool: {credits.workspace.remaining} of {credits.workspace.limit} left
          {credits.workspace.reserved ? ` (${credits.workspace.reserved} processing)` : ''}
          {' '}&mdash; shared with everyone here.
        </p>
      )}
    </section>
  );
}
